import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { ExpandableText } from '../CollapsibleText/ExpandableText';
import ParentPostBlock from './ParentPostBlock';
import { ImagesContainer } from '../Image/ImagesContainer';
import PublicationTranslateButton from './PublicationTranslateButton';
import { platformIconsMapping } from '../../utils/platforms';
import { metricSupport } from '../../utils/metrics';
import { decodeSourceName } from '../../utils/decodeURI';
import { API } from '../../API';
import { ReactComponent as ViewsIcon } from '../../assets/views.svg';
import { ReactComponent as EngagementIcon } from './assets/engagement.svg';
import { ReactComponent as CommentsIcon } from '../MessagesTable/assets/comment-clear.svg';
import { ReactComponent as RepostsIcon } from '../MessagesTable/assets/repost-clear.svg';
import { ReactComponent as ReactionsIcon } from './assets/reactions.svg';
import { ReactComponent as CalendarIcon } from '../../assets/calendar.svg';
import { ReactComponent as FollowersIcon } from '../../assets/followers.svg';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as ActorIcon } from '../../pages/SearchPage/assets/link.svg';
import { ReactComponent as PersonIcon } from '../../pages/SearchPage/assets/person.svg';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import styles from './PublicationCard.module.scss';

function isMetricSupported(sourceType, metric, contentType) {
  return metricSupport[metric]?.[sourceType]?.[contentType] ?? false;
}

const PublicationCard = ({ message, isBulkTranslated = false, nativeLanguage }) => {
  const { t } = useTranslation();
  const [isLocallyTranslated, setIsLocallyTranslated] = useState(false);
  const [isOptedOutOfBulk, setIsOptedOutOfBulk] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  // Cache fetched translations locally so re-toggling doesn't re-fetch
  const [translatedText, setTranslatedText] = useState(message.translated ?? null);
  const [translatedParentText, setTranslatedParentText] = useState(
    message.parent_message?.translated ?? null,
  );

  const showTranslation = isLocallyTranslated || (isBulkTranslated && !isOptedOutOfBulk);

  const sourceType = message.source?.source_type;
  const contentType = message.content_type;

  const additionalTooltipInfo = (metric) => {
    const supported = isMetricSupported(sourceType, metric, contentType);
    if (supported === false) return t("Platform doesn't provide this metric");
    if (supported === null) return t('Metric not collected for this platform');
    return t('Data may update with a delay');
  };

  const fetchTranslation = useCallback(async (markAsLocallyTranslated = true) => {
    if (!nativeLanguage) return;
    setIsTranslating(true);
    try {
      const hasParent = !!message.parent_message?.text;
      const body = (text) => ({ text, destination_language: nativeLanguage });

      if (hasParent) {
        const [parentResult, mainResult] = await Promise.allSettled([
          API.fetch('POST', '/API/v1/translations/translate', null, body(message.parent_message.text)),
          API.fetch('POST', '/API/v1/translations/translate', null, body(message.text)),
        ]);
        if (parentResult.status === 'fulfilled') {
          setTranslatedParentText(parentResult.value.destination_text);
        }
        if (mainResult.status === 'fulfilled') {
          setTranslatedText(mainResult.value.destination_text);
        }
      } else {
        const result = await API.fetch('POST', '/API/v1/translations/translate', null, body(message.text));
        setTranslatedText(result.destination_text);
      }
    } finally {
      setIsTranslating(false);
      if (markAsLocallyTranslated) {
        setIsLocallyTranslated(true);
      }
    }
  }, [message, nativeLanguage]);

  const handleTranslate = useCallback((wantTranslation) => {
    if (isBulkTranslated) {
      if (!wantTranslation) {
        setIsOptedOutOfBulk(true);
        setIsLocallyTranslated(false);
      } else {
        setIsOptedOutOfBulk(false);
      }
      return;
    }
    if (!wantTranslation) {
      setIsLocallyTranslated(false);
      return;
    }
    if (translatedText) {
      setIsLocallyTranslated(true);
    } else {
      fetchTranslation();
    }
  }, [isBulkTranslated, translatedText, fetchTranslation]);

  // Auto-translate when bulk is enabled and this card hasn't been translated yet
  useEffect(() => {
    if (isBulkTranslated && !translatedText && !isTranslating) {
      fetchTranslation(false);
    }
    if (!isBulkTranslated) {
      setIsOptedOutOfBulk(false);
    }
  }, [isBulkTranslated, translatedText, isTranslating, fetchTranslation]);

  const displayText = showTranslation
    ? (translatedText ?? message.translated ?? message.text)
    : message.text;

  const platformIcon = platformIconsMapping[sourceType] ?? null;

  const rawDate = message.date_publicated || message.published_at;
  const formattedDate = rawDate
    ? format(
        parseISO(message.date_publicated ? rawDate : rawDate + 'Z'),
        'dd LLL yyyy HH:mm',
      )
    : null;

  return (
    <article
      className={styles.card}
      aria-label={t('Publication from {{name}}', { name: decodeSourceName(message.source?.name) ?? '' })}
    >
      {/* ---- Header row ---- */}
      <div className={styles.header}>
        {platformIcon && (
          <span className={styles.headerItem} aria-hidden="true">
            {platformIcon}
          </span>
        )}

        {formattedDate && (
          <span
            className={styles.headerItem}
            data-tooltip-id="message-publication-date"
            data-tooltip-content={t('Publication date')}
          >
            <CalendarIcon aria-hidden="true" />
            {formattedDate}
          </span>
        )}

        {message.source?.followers != null && (
          <span
            className={styles.headerItem}
            data-tooltip-id="message-followers"
            data-tooltip-content={t('Followers')}
          >
            <FollowersIcon aria-hidden="true" />
            {formatWithCustomGigaBillion(message.source.followers)}
          </span>
        )}

        {message.source?.name && (
          <span
            className={styles.headerItem}
            data-tooltip-id="source-info"
            data-tooltip-content={`${decodeSourceName(message.source.name)}\n${
              message.source.url ?? t('N/A')
            }`}
          >
            <ActorIcon aria-hidden="true" />
            {message.url || message.message_url ? (
              <a
                href={message.url ?? message.message_url}
                target="_blank"
                rel="noreferrer"
                className={styles.sourceName}
              >
                {decodeSourceName(message.source.name)}
              </a>
            ) : (
              <span className={styles.sourceName}>
                {decodeSourceName(message.source.name)}
              </span>
            )}
          </span>
        )}

        {message.actor && (
          <span
            className={styles.headerItem}
            data-tooltip-id="actor-info"
            data-tooltip-content={`${decodeSourceName(message.actor.name)}\n${
              message.actor.url ?? t('N/A')
            }`}
          >
            <PersonIcon aria-hidden="true" />
            <span className={styles.sourceName}>
              {decodeSourceName(message.actor.name)}
            </span>
          </span>
        )}

        {(message.url || message.message_url) && (
          <a
            href={message.url ?? message.message_url}
            target="_blank"
            rel="noreferrer"
            className={styles.externalLink}
            data-tooltip-id="publication-link"
            data-tooltip-content={t('Go to publication')}
            aria-label={t('Go to publication')}
          >
            <LinkIcon aria-hidden="true" />
          </a>
        )}
      </div>

      {/* ---- Separator ---- */}
      <div className={styles.separator} role="presentation" />

      {/* ---- Metrics row ---- */}
      <div className={styles.metricsRow}>
        <div className={styles.metrics}>
          <span
            className={styles.metric}
            data-tooltip-id="message-views"
            data-tooltip-content={`${t('Views')}\n ${additionalTooltipInfo(
              'views',
            )}`}
          >
            <ViewsIcon aria-hidden="true" />
            {isMetricSupported(sourceType, 'views', contentType)
              ? formatWithCustomGigaBillion(
                  message.impressions ?? message.views ?? 0,
                )
              : t('N/A')}
          </span>
          <span
            className={styles.metric}
            data-tooltip-id="message-comments"
            data-tooltip-content={`${t('Comments')}\n ${additionalTooltipInfo(
              'comments',
            )}`}
          >
            <CommentsIcon aria-hidden="true" />
            {isMetricSupported(sourceType, 'comments', contentType)
              ? formatWithCustomGigaBillion(
                  message.replies || message.comments || 0,
                )
              : t('N/A')}
          </span>
          <span
            className={styles.metric}
            data-tooltip-id="message-reposts"
            data-tooltip-content={`${t('Reposts')}\n ${additionalTooltipInfo(
              'reposts',
            )}`}
          >
            <RepostsIcon aria-hidden="true" />
            {isMetricSupported(sourceType, 'reposts', contentType)
              ? formatWithCustomGigaBillion(
                  message.forwards || message.shares || 0,
                )
              : t('N/A')}
          </span>
          <span
            className={styles.metric}
            data-tooltip-id="message-reactions"
            data-tooltip-content={`${t('Reactions')}\n ${additionalTooltipInfo(
              'reactions',
            )}`}
          >
            <ReactionsIcon aria-hidden="true" />
            {isMetricSupported(sourceType, 'reactions', contentType)
              ? formatWithCustomGigaBillion(message.reactions ?? 0)
              : t('N/A')}
          </span>
          <span
            className={styles.metric}
            data-tooltip-id="message-engagement"
            data-tooltip-content={`${t('Engagement')}\n ${t(
              'Total amount of comments, reposts and reactions',
            )}`}
          >
            <EngagementIcon aria-hidden="true" />
            {isMetricSupported(sourceType, 'engagement', contentType)
              ? message.engagement
                ? formatWithCustomGigaBillion(message.engagement)
                : formatWithCustomGigaBillion(
                    parseInt(message.forwards ?? 0) +
                      parseInt(message.reactions ?? 0) +
                      parseInt(message.replies ?? 0),
                  )
              : t('N/A')}
          </span>
        </div>
        <PublicationTranslateButton
          isTranslated={showTranslation}
          isLoading={isTranslating}
          onToggle={handleTranslate}
        />
      </div>

      {/* ---- Main images ---- */}
      {message.images?.length > 0 && (
        <div className={styles.textBlock}>
          <div className={styles.imagesBlock}>
            <ImagesContainer images={message.images} showCount={3} />
          </div>
        </div>
      )}

      {/* ---- Quoted parent post ---- */}
      {message.parent_message && (
        <div className={styles.textBlock}>
          <ParentPostBlock
            parentMessage={message.parent_message}
            text={
              showTranslation
                ? (translatedParentText ?? message.parent_message?.translated ?? message.parent_message?.text)
                : message.parent_message?.text
            }
          />
        </div>
      )}

      {/* ---- Main text ---- */}
      <div className={styles.textBlock}>
        <ExpandableText
          text={displayText ?? ''}
          length={120}
          textClassName={styles.expandableText}
        />
      </div>
    </article>
  );
};

export default PublicationCard;
